/**
 * Type-safe environment variables configuration
 * This ensures all environment variables are defined with proper types
 *
 * Type definitions are in vite-env.d.ts
 */

/**
 * Get environment variable with type safety
 */
function getEnv<K extends keyof ImportMetaEnv>(key: K): ImportMetaEnv[K] {
  return import.meta.env[key]
}

/**
 * Type-safe environment variable getters
 */
const env = {
  /**
   * API base URL for backend requests
   * Required in production - optional in dev mode
   */
  get apiBaseUrl(): string {
    const url = getEnv('VITE_API_BASE_URL')
    // Vite env vars are strings; treat only "true" as enabled.
    const devModeEnabled = import.meta.env.VITE_DEV_MODE === 'true'
    if (!url && !devModeEnabled) {
      throw new Error('VITE_API_BASE_URL is required but not set')
    }
    // In dev mode, return a dummy URL (won't be used)
    return url || 'http://localhost:8000'
  },

  /**
   * Whether the app is running in development mode
   */
  get isDev(): boolean {
    return getEnv('DEV')
  },

  /**
   * Current mode (development, production, etc.)
   */
  get mode(): string {
    return getEnv('MODE')
  },

  /**
   * Whether the app is running in production mode
   */
  get isProd(): boolean {
    return getEnv('PROD')
  },
} as const

/**
 * Validate required environment variables
 * Call this at app startup to ensure required vars are set
 */
function validateEnv(): void {
  // Skip validation in dev mode
  if (import.meta.env.VITE_DEV_MODE === 'true') {
    return
  }

  const errors: string[] = []

  // VITE_API_BASE_URL is required
  try {
    env.apiBaseUrl
  } catch {
    errors.push('VITE_API_BASE_URL is required')
  }

  if (errors.length > 0) {
    throw new Error(`Environment validation failed:\n${errors.map((e) => `  - ${e}`).join('\n')}`)
  }
}

export { env, validateEnv }
